import {clearList, getSelectedListItems} from '@Components/list/list-reducer';
import type {PromptBoxConfigOptionType, PromptBoxConfigOptionValue, SelectedValueType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {GENERATION_OPTIONS_COMPONENT_ID} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {getDefaultValueForOption, getListIdForConfigOption, getOptionTypeFromListId, getSelectedValue} from '@Libraries/ai-text-prompt-box-library';
import {useAppDispatch, useAppSelector} from '@/hooks';

export interface UsePromptConfigOptionsType {
  clearAllLists: VoidFunction;
  selectedValues: SelectedValueType;
  cachePromptOptionsUsedForGeneration: VoidFunction;
  getCachedValues: () => SelectedValueType;
  defaultValues: SelectedValueType;
  deleteCacheValues: VoidFunction;
}

/**
 * @author Moeed Ahmad <moeed@250mils.com>
 * */
const usePromptConfigOptions = (
  optionsToUse: PromptBoxConfigOptionType[],
  presets: SelectedValueType | null = null,
  promptConfigOptionInstanceId = GENERATION_OPTIONS_COMPONENT_ID
): UsePromptConfigOptionsType => {
  const dispatch = useAppDispatch();

  const convertArrayToObject = (array: SelectedValueType[]): SelectedValueType => {
    return array.reduce((acc, item) => {
      const key = Object.keys(item)[0];
      acc[key] = item[key];
      return acc;
    }, {});
  };

  const defaultOptions = convertArrayToObject(
    optionsToUse.map((option: PromptBoxConfigOptionType) => {
      return {[option]: getDefaultValueForOption(option)};
    })
  );

  const reduxListState = useAppSelector((state) => {
    return state.list;
  });

  const listIds = optionsToUse.map((option) => {
    return getListIdForConfigOption(promptConfigOptionInstanceId, option);
  });

  const getCachedOptions = (): SelectedValueType => {
    return window.PMW.getCacheItem(GENERATION_OPTIONS_COMPONENT_ID) as unknown as SelectedValueType;
  };

  const selectedValues = listIds.map((listId): SelectedValueType => {
    const optionType = getOptionTypeFromListId(listId);
    return {
      [optionType]: getSelectedValue(getSelectedListItems(reduxListState, listId), presets ? presets[optionType] : defaultOptions[optionType]) as PromptBoxConfigOptionValue,
    };
  });

  const clearAllLists = (): void => {
    listIds.forEach((listId) => {
      dispatch(clearList({id: listId}));
    });
  };

  const cacheOptions = (): void => {
    window.PMW.setCacheItem(GENERATION_OPTIONS_COMPONENT_ID, convertArrayToObject(selectedValues));
  };

  const deleteCacheOptions = (): void => {
    window.PMW.deleteCacheItem(GENERATION_OPTIONS_COMPONENT_ID);
  };

  return {
    clearAllLists,
    selectedValues: convertArrayToObject(selectedValues),
    getCachedValues: getCachedOptions,
    defaultValues: defaultOptions,
    cachePromptOptionsUsedForGeneration: cacheOptions,
    deleteCacheValues: deleteCacheOptions,
  };
};

export default usePromptConfigOptions;
